import { useState } from "react";
import { toast } from "react-toastify";
import Select from "../../../components/select";
import { useDispatch, useSelector } from "react-redux";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import { Link, useNavigate } from "react-router-dom";
import Modal from "../../../components/modal";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { deleteVolumeBackup, restoreVolumeBackup } from "../../../hooks/local/userReducer";
import { useVolumeBackupsList, useVolumeList } from "../layout/resuableEffect";
import PaymentModal from "../../../components/paymentModal";
import usePartnerNavigation from "../../../hooks/usePartnerNavigation";


const ManageVolumeBackups = () => {

  document.title = "Manage Volume Backups - CloudVantage";

  const dispatch = useDispatch();
  const { navigate, generatePartnerUrl } = usePartnerNavigation();


  const projectId = useSelector((state) => state.user.selectedProject)?.id;
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? Number(state.user.virtualAccount.dollar_balance) || 0
        : Number(state.user.virtualAccount.balance) || 0
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);

  const options = [
    { value: "all-backups", label: "All backups" },
    { value: "name", label: "Name" },
    { value: "status", label: "Status" },
  ];

  const [manageBackupModal, setManageBackupModal] = useState(false);
  const [restoreModal, setRestoreModal] = useState(false);
  const [isDeleteImageModal, setIsDeleteImageModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoreToNew, setRestoreToNew] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const volumeBackupList = useVolumeBackupsList(projectId);
  const volumeList = useVolumeList(projectId);

  const volumeBackupListMap = Array.isArray(volumeBackupList)
    ? volumeBackupList.map((volume, index) => ({
        sn: index + 1,
        id: volume?.id,
        name: volume?.name || volume?.display_name || volume?.backup_name || "N/A",
        volumeName: volume?.volume_name || volume?.volume?.name || "N/A",
        description: volume?.description || volume?.display_description || "N/A",
        status: volume?.status || "N/A",
        size: volume?.size || "N/A",
        groupSnapshot: volume?.groupSnapshot || "N/A",
        date: volume?.created_on,
      }))
    : [];

  const availableVolumes = Array.isArray(volumeList)
    ? volumeList
        .filter((volume) => volume.status === "available")
        .map((volume) => ({
          value: volume.id,
          label: volume.name || "Default Volume",
        }))
    : [];

  const showManageBackupModal = (key, data) => {
    setManageBackupModal(key);
    setSelectedBackup(data);
    sessionStorage.setItem("selectedVolumeBackup", JSON.stringify({ snapshotId: data.id, snapshotName: data.name }));
  };

  const closeManageBackupModal = () => setManageBackupModal(false);
  const openRestoreModal = () => {
    setRestoreModal(true);
    closeManageBackupModal();
  };
  const closeRestoreModal = () => setRestoreModal(false);

  const [searchText, setSearchText] = useState("");
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const filteredData = volumeBackupListMap?.filter((row) =>
    row?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    row?.status?.toLowerCase().includes(searchText.toLowerCase())
  );

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);

  const handleServerAction = async (action, backupId) => {
    const { payload } = await dispatch(action({ projectId, backupId }));
    if (payload?.statuscode === "00" || payload?.statuscode === 200) {
      toast.success("Action successful!", { theme: "colored" });
      window.location.reload();
    } else {
      const errorMsg = payload?.statusmessage || payload?.message || "Action failed";
      toast.error(errorMsg, { theme: "colored" });
    }
  };

  const deleteBackup = (backupId) => handleServerAction(deleteVolumeBackup, backupId);

  const calculateEstimatedCost = (volumeSize, currency, paymentMethod = "monthly") => {
    if (!volumeSize) return { formatted: "0.00", numeric: 0 };

    const volumeSizePrice = currency === "NGN" ? 300 : 0.2;
    const volumeSizeValue = parseFloat(volumeSize);

    let totalCost = volumeSizeValue * volumeSizePrice;

    if (paymentMethod === "yearly") {
      const yearlyPrice = totalCost * 12;
      const discount = yearlyPrice * 0.01;
      totalCost = yearlyPrice - discount;
    }

    return {
      formatted: totalCost.toLocaleString("en-US", {
        style: "currency",
        currency: currency === "NGN" ? "NGN" : "USD",
      }),
      numeric: totalCost,
    };
  };

  const restoreFormik = useFormik({
    initialValues: {
      volume: "",
      newVolumeName: "",
    },
    validationSchema: Yup.object().shape({
      volume: Yup.string().required("Volume selection is required"),
      newVolumeName: Yup.string().when("volume", {
        is: (val) => val === "new",
        then: (schema) => schema.required("New volume name is required"),
      }),
    }),
    onSubmit: async (values) => {
      const backupId = selectedBackup?.id;
      const restoreData = values.volume === "new"
        ? { 
            name: values.newVolumeName, 
            size: selectedBackup?.size, 
            backupId
          }
        : { volume_id: values.volume, backupId };

      const { payload } = await dispatch(restoreVolumeBackup({ data: restoreData, projectId }));
      console.log("Restore payload:", payload);
      if (payload?.statuscode === "00" || payload?.statuscode === 200) {
        toast.success("Volume restoration initiated successfully!", { theme: "colored" });
        navigate(`/client-area/volumes`);
      } else {
        const errorMsg = payload?.statusmessage || payload?.message || "Failed to restore volume backup";
        toast.error(errorMsg, { theme: "colored" });
      }
    },
  });

  const handleRestoreBackupClick = (e) => {
    e.preventDefault();
    // Students are not billed, so we bypass the wallet check and proceed to restore
    restoreFormik.handleSubmit();
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue">Manage Volume Backups</p>

      {volumeBackupListMap?.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
            <div className="md:col-span-3">
              <Select label="Filter backups" name="backups" options={options} />
            </div>
            <div className="md:col-span-4">
              <input
                type="search"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search by mode of payment, status..."
                className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
            <table className="w-full text-sm text-left overflow-hidden">
              <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                <tr>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">S/N</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Name</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Description</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Size</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Status</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Volume Name</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                {currentEntries.map((row, index) => (
                  <tr key={index} className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap">
                    <td className="text-start py-4 px-6">{row?.sn}.</td>
                    <td className="text-start py-4 px-6">{row?.name}.</td>
                    <td className="text-start py-4 px-6">{row?.description}</td>
                    <td className="text-start py-4 px-6">{row?.size}</td>
                    <td className="text-start py-4 px-6">{row?.status}</td>
                    <td className="text-start py-4 px-6">{row?.volumeName}</td>
                    <td className="text-start py-4 px-6">
                      <button
                        className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                        onClick={() => showManageBackupModal(index, row)}
                      >
                        Manage Backup
                      </button>
                      {manageBackupModal === index && (
                        <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                            <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                              <div className="flex gap-2 items-center">
                                <span className="dark:text-white">Manage "{row.name}"</span>
                              </div>
                              <img
                                src={close}
                                onClick={closeManageBackupModal}
                                className="h-5 cursor-pointer"
                                alt=""
                              />
                            </div>
                            <div className="p-4 grid gap-1">
                              <Link
                                to="#"
                                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
                                onClick={openRestoreModal}
                              >
                                <span>Restore backup</span>
                                <span><img src={arrow} alt="" className="" /></span>
                              </Link>
                              <div
                                className="flex justify-self-center text-red-500 rounded-md py-5 px-3 text-center cursor-pointer"
                                onClick={() => setIsDeleteImageModal(true)}
                              >
                                <span className="hover:underline">Delete volume backup</span>
                              </div>
                            </div>
                            <Modal
                              title={"Delete Backup"}
                              isVisible={isDeleteImageModal}
                              onClose={() => setIsDeleteImageModal(false)}
                            >
                              <div className="text-sm text-center pt-6 text-wrap">
                                Are you sure you want to delete <span className="underline">{row.name}</span>. This action cannot be undone.
                              </div>
                              <div className="grid gap-4 justify-center py-6">
                                <button
                                  className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                  onClick={() => deleteBackup(row.id)}
                                >
                                  Yes, delete {row.name}
                                </button>
                                <button
                                  className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                  onClick={() => setIsDeleteImageModal(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </Modal>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
            <p className="text-gray-400 text-sm">
              Showing <span className="font-medium text-black dark:text-white">{indexOfFirstEntry + 1}</span> to <span className="font-medium text-black dark:text-white">{Math.min(indexOfLastEntry, volumeBackupListMap?.length)}</span> of <span className="font-medium text-black dark:text-white">{volumeBackupListMap?.length}</span> results
            </p>
            <div className="flex gap-3">
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"}`}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"}`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="md:px-52 text-center grid text-gray-500 mt-32">
          It looks like you don't have a volume backup yet. Once you make a backup for a volume, your volume backup details will appear here.
        </div>
      )}

      {restoreModal && selectedBackup && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full lg:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Restore Volume Backup</p>
              <span onClick={closeRestoreModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid">
              <Select
                label="Select Volume"
                name="volume"
                options={[
                  { value: "", label: "Select a Volume" }, 
                  { value: "new", label: "Create a New Volume" }, 
                  ...availableVolumes
                ]}
                value={restoreFormik.values.volume}
                onChange={(e) => {
                  restoreFormik.handleChange(e);
                  setRestoreToNew(e.target.value === "new");
                  setSelectedVolume(e.target.value !== "new" ? e.target.value : "");
                }}
                onBlur={restoreFormik.handleBlur}
                onError={restoreFormik.errors.volume && restoreFormik.touched.volume ? restoreFormik.errors.volume : null}
              />
              {restoreToNew && (
                <Input
                  label="New Volume Name"
                  type="text"
                  name="newVolumeName"
                  value={restoreFormik.values.newVolumeName}
                  onChange={restoreFormik.handleChange}
                  onBlur={restoreFormik.handleBlur}
                  onError={restoreFormik.errors.newVolumeName && restoreFormik.touched.newVolumeName ? restoreFormik.errors.newVolumeName : null}
                />
              )}
              {restoreToNew && selectedBackup?.size && (
                <p className="text-sm text-gray-600">
                  Estimated Price: {calculateEstimatedCost(selectedBackup?.size, currency).formatted}
                </p>
              )}
              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText="Cancel"
                  buttonRole="submit"
                  handleClick={closeRestoreModal}
                  className="bg-gray-500 text-white text-sm px-6 py-4 rounded-sm"
                />
                <Button
                  buttonRole="submit"
                  buttonText="Restore Backup to Volume"
                  handleClick={handleRestoreBackupClick}
                  className="bg-[#1DA1F2] text-white text-sm px-6 py-4 rounded-sm"
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={calculateEstimatedCost(selectedBackup?.size, currency).numeric}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => restoreFormik.handleSubmit()}
      />
    </div>
  );
};

export default ManageVolumeBackups;